import type { CareLogDetail } from './-care-log-detail-sheet'
import type {
    CareLogCardArea,
    CareLogCardMaterial,
} from './-care-log-list-card'
import type { MediaItem } from '@/components/common/media-lightbox'
import type {
    CareLog,
    CareLogActivityType,
    CareLogFile,
} from '@/lib/api/care-logs-api'
import { m } from '@/lib/paraglide/messages'

/** Ngày giờ lưu dạng wall-clock (không quy đổi múi giờ) nên cắt chuỗi, không
 *  new Date() — đúng như bảng desktop vẫn hiển thị. */
export const formatCareLogDate = (s: string) => s.slice(0, 16).replace('T', ' ')

const amountLabels: Partial<Record<CareLogActivityType, () => string>> = {
    fertilizing: m.care_log_amount_fertilizing,
    spraying: m.care_log_amount_spraying,
    harvesting: m.care_log_amount_harvesting,
}

/** Nhãn của ô định lượng trên card — hộ dân đọc "đã bón"/"đã phun" tự nhiên hơn
 *  một chữ "Khối lượng" chung chung. */
export function careLogAmountLabel(activity: CareLogActivityType): string {
    return (amountLabels[activity] ?? m.care_log_amount_used)()
}

/** Vùng chè của nhật ký. API cũ (trước khi endpoint danh sách hydrate vùng)
 *  không có `areas` → card chỉ mất phần vùng, không hỏng cả trang. */
export function careLogCardAreas(log: CareLog): CareLogCardArea[] {
    return (log.areas ?? []).map((a) => ({
        id: a.id,
        name: a.name,
        acreage: a.acreage,
        paths: a.paths,
        markers: a.markers,
    }))
}

/**
 * Các dòng định lượng hiện trên card. Bón phân/Phun thuốc lấy theo TỪNG vật tư
 * (`quantity` tổng của nhật ký cộng lẫn đơn vị nên vô nghĩa). Hái chè không có
 * vật tư nên quy sản lượng thành một dòng để card lúc nào cũng trả lời được
 * "đợt này bao nhiêu".
 */
export function careLogCardMaterials(log: CareLog): CareLogCardMaterial[] {
    // API trả `materials: null` cho nhật ký không có vật tư (Go nil slice) —
    // đọc thẳng .length là sập cả trang danh sách.
    const materials = log.materials ?? []
    if (materials.length > 0) {
        return materials.map((mat) => ({
            name: mat.material_name || m.care_log_material_deleted(),
            quantity: mat.quantity,
            unit: mat.material_unit,
            selfBought: !!mat.self_bought,
        }))
    }
    if (log.activity_type === 'harvesting' && log.quantity > 0) {
        return [
            {
                name: m.care_log_harvest_quantity(),
                quantity: log.quantity,
                unit: 'kg',
            },
        ]
    }
    return []
}

/**
 * Ảnh/video minh chứng ở dạng lightbox đọc được. `mime_type` quyết định render
 * `<img>` hay `<video>`; tệp cũ thiếu mime thì coi là ảnh (đúng với đa số dữ
 * liệu lịch sử) chứ không bị loại khỏi danh sách.
 */
export function careLogMediaItems(files?: CareLogFile[]): MediaItem[] {
    return (files ?? []).map((file) => ({
        id: file.id,
        url: file.url,
        name: file.name,
        mimeType: file.mime_type,
    }))
}

/**
 * Dựng dữ liệu cho màn chi tiết từ chính dòng danh sách — mở chi tiết không cần
 * chờ request nào.
 *
 * `detail` là bản ghi từ endpoint chi tiết, truyền vào khi nó ĐÃ về. Nhận cả
 * bản ghi chứ không chỉ mảng `files` là có chủ đích: Go serialise `files` với
 * `omitempty` nên nhật ký không còn tệp nào trả về thiếu hẳn field, không phân
 * biệt được với "chưa tải xong" nếu chỉ nhìn vào mảng.
 */
export function careLogDetail(log: CareLog, detail?: CareLog): CareLogDetail {
    return {
        id: log.id,
        grower: log.grower_name || '—',
        activity: log.activity_type,
        logDate: formatCareLogDate(log.log_date),
        implementers: (log.implementer_names ?? []).join(', '),
        areas: careLogCardAreas(log),
        materials: careLogCardMaterials(log),
        amountLabel: careLogAmountLabel(log.activity_type),
        note: log.note,
        quarantineNote: log.quarantine_note,
        // Endpoint danh sách đã trả ảnh minh chứng, nên chi tiết mở ra là có
        // ảnh ngay; chi tiết về rồi thì nó là nguồn đúng, KỂ CẢ khi nó nói
        // không còn tệp nào.
        files: careLogMediaItems(detail ? detail.files : log.files),
    }
}
